/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useContext } from 'react';
import IconButton from '@mui/material/IconButton';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  selectIsRequestingFlightPath,
  selectIsRequestingTailDetailsPanelTerminal,
  selectMapRulerToolShown,
  selectTailDetailsPanelTerminal,
} from '../../../store';
import StyledProps from '../../common/theme/AppThemeInterface';
import { MapActionsContext, useSelectorWithMapId } from '../../common/map/BaseMapContainer';
import { Straighten } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import appTheme from '../../common/theme/appTheme.json';

const BUTTON_HEIGHT = 32;
const FONT_SIZE = 18;
const PADDING = (BUTTON_HEIGHT - FONT_SIZE) / 2;

const RulerToolButtonWrapper = styled.span`
  height: ${BUTTON_HEIGHT}px;
  background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  border: none;
  border-radius: 5px;
  margin-left: 6px;

  .ftv__ruler__tool__button {
    color: ${({ theme }: StyledProps) => theme.primaryTextColor};
    height: ${BUTTON_HEIGHT}px;
    padding: ${PADDING}px;
  }

  .ftv__ruler__tool__button__icon {
    font-size: ${FONT_SIZE}px;
    color: ${(props: any) =>
      props.mapRulerToolShown ? props.theme.buttonSelectedColor : props.theme.primaryTextColor};
  }
`;

const FullTailViewRulerToolButton = () => {
  const dispatch = useDispatch();
  const isRequestingTailDetailsPanelTerminal = useSelector(selectIsRequestingTailDetailsPanelTerminal, shallowEqual);
  const tailDetailsPanelTerminal = useSelector(selectTailDetailsPanelTerminal, shallowEqual);
  const isRequestingFlightPath = useSelector(selectIsRequestingFlightPath, shallowEqual);

  const mapActionsContext = useContext(MapActionsContext);
  const mapRulerToolShown = useSelectorWithMapId(selectMapRulerToolShown);
  const { toggleMapRulerTool } = mapActionsContext.mapActions;

  const followAircraft = true;

  const restrictionEnabled = tailDetailsPanelTerminal && tailDetailsPanelTerminal.restrictionEnabled;
  return (
    <RulerToolButtonWrapper mapRulerToolShown={mapRulerToolShown}>
      {restrictionEnabled ? null : (
        <Tooltip
          arrow
          title="Measure Distance From Aircraft"
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: { backgroundColor: appTheme.tooltipBackgroundColor },
            },
            arrow: {
              sx: { color: appTheme.tooltipBackgroundColor },
            },
          }}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -5],
                  },
                },
              ],
            },
          }}
        >
          {/* Wrap IconButton in div to prevent console warning about providing disabled button to Tooltip */}
          <div>
            <IconButton
              className="ftv__ruler__tool__button"
              onClick={() => {
                dispatch(toggleMapRulerTool(followAircraft));
              }}
              disabled={isRequestingFlightPath || isRequestingTailDetailsPanelTerminal}
            >
              <Straighten data-test="ftv__ruler__tool__button" className="ftv__ruler__tool__button__icon" />
            </IconButton>
          </div>
        </Tooltip>
      )}
    </RulerToolButtonWrapper>
  );
};

export default FullTailViewRulerToolButton;
